import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiAlertCircle,
  FiAperture,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import { t, useI18n } from "./utils/i18n";
import StarBackground from "./star";
import LanguageSwitch from "./LanguageSwitch";

const AUTH_STAR_BACKGROUND_STYLE = { zIndex: 1, opacity: 0.34 };

// Repeated primitives as Tailwind classes. Radius flows through the unified
// scale (rounded-control = --r-md, rounded-field = --r-sm, rounded-panel =
// --r-lg). Bespoke gradients/backgrounds stay as inline style where a Tailwind
// arbitrary value would only be longer and less readable.
// `auth-input` (index.css) keeps browser autofill on the dark theme. The
// `--brand-*` fills follow the day/night scene (src/scene/brandSurfaces.css).
export const authInputClass =
  "auth-input w-full box-border px-4 py-3 rounded-control border border-solid border-[rgba(226,232,240,0.18)] bg-[var(--brand-field)] text-slate-50 outline-none text-[0.95rem] focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/30";

// Replaces {name} placeholders in a translated auth string.
export const formatAuthText = (key, values = {}) =>
  Object.entries(values).reduce(
    (text, [name, value]) => text.split(`{${name}}`).join(String(value)),
    t(key)
  );

// The API's new-password rule (passwordAuth.PASSWORD_PATTERN): printable ASCII
// without spaces, with an uppercase and a lowercase letter, a number and a symbol.
export const NEW_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[\x21-\x7E]{8,}$/;
export const PASSWORD_ALLOWED_CHARS = /^[\x21-\x7E]*$/;
const PASSWORD_SYMBOL = /[!-/:-@[-`{-~]/;

export const authLabelClass = "block text-slate-300 text-[0.82rem] font-bold mb-1.5";

// sky-400 matches the in-modal switch links and stays readable on the dark panel.
export const authPageLinkClass = "text-sky-400 font-bold no-underline";

export const authButtonClass = (disabled = false, secondary = false) =>
  [
    "w-full rounded-control px-4 py-[0.95rem] font-bold text-[0.95rem] border border-solid transition-colors",
    secondary
      ? "bg-[var(--brand-control)] text-slate-200 border-[rgba(148,163,184,0.2)]"
      : "bg-[linear-gradient(110deg,#8fd3ff_0%,#d9f99d_48%,#fed7aa_100%)] text-[#075985] border-[rgba(255,255,255,0.32)]",
    disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer",
  ].join(" ");

export function AuthPageShell({
  message,
  onDismissMessage,
  children,
  footer,
}) {
  const { language: currentLanguage } = useI18n();
  const translate = (key) => t(key, currentLanguage);
  return (
    <div className="relative min-h-screen overflow-x-hidden overflow-y-auto bg-[#01040b]">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.82] bg-center bg-cover"
        style={{ backgroundImage: "url('/images/jolymei-auth-hero.png')" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 72% 28%, rgba(34,197,94,0.22), transparent 22%), radial-gradient(circle at 86% 70%, rgba(249,115,22,0.2), transparent 20%), radial-gradient(circle at 42% 48%, rgba(245,158,11,0.12), transparent 18%)",
          mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(2,6,23,0.24) 0%, rgba(2,6,23,0.52) 48%, rgba(2,6,23,0.86) 100%), linear-gradient(180deg, rgba(2,6,23,0.1) 0%, rgba(2,6,23,0.86) 100%)",
        }}
      />
      <StarBackground starCount={30} isLoading={false} containerStyle={AUTH_STAR_BACKGROUND_STYLE} />

      <div className="absolute top-6 right-6 z-20">
        <LanguageSwitch variant="glass" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1140px] flex-wrap items-center gap-12 pt-24 px-5 pb-16">
        <div className="min-h-full flex-[1_1_430px] text-white">
          <div className="mb-[1.7rem] flex items-center gap-[0.9rem]">
            <div className="flex h-[3.3rem] w-[3.3rem] items-center justify-center rounded-control border border-[rgba(103,232,249,0.24)] bg-[rgba(6,182,212,0.16)]">
              <FiAperture color="#67e8f9" size={24} />
            </div>
          </div>

          <h1 className="m-0 mb-[1.05rem] max-w-[8ch] text-[5.7rem] leading-[0.95] tracking-normal">
            {translate("auth.shell.title")}
          </h1>
          <p className="m-0 max-w-[31rem] text-[1.15rem] leading-[1.62] text-slate-300">
            {translate("auth.shell.subtitle")}
          </p>
          <div className="mt-[1.35rem] inline-flex items-center rounded-pill border border-[rgba(103,232,249,0.2)] bg-[rgba(8,47,73,0.42)] px-[0.85rem] py-[0.55rem] text-[0.88rem] font-bold text-[#b6e6f5]">
            {translate("auth.shell.tool_note")}
          </div>
        </div>

        <div className="w-full max-w-[480px] flex-[1_1_360px]">
          <AuthPanel message={message} onDismissMessage={onDismissMessage} footer={footer}>
            {children}
          </AuthPanel>
        </div>
      </div>
    </div>
  );
}

export function AuthPanel({ message, onDismissMessage, children, footer }) {
  const translate = (key) => t(key);

  return (
    <div
      className="w-full rounded-panel border border-[rgba(226,232,240,0.18)] p-6"
      style={{
        background: "var(--brand-panel)",
        boxShadow: "0 14px 36px rgba(2,6,23,0.26), inset 0 1px 0 rgba(255,255,255,0.08)",
      }}
    >
      {message ? (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-control border border-[#dc262622] bg-[rgba(127,29,29,0.35)] px-4 py-[0.9rem] text-[#fecaca]">
          <div className="flex gap-[0.7rem]">
            <FiAlertCircle size={18} className="mt-[0.1rem] shrink-0" />
            <div className="text-[0.92rem] leading-[1.5]">{message}</div>
          </div>
          {onDismissMessage ? (
            <button
              type="button"
              onClick={onDismissMessage}
              className="cursor-pointer border-none bg-transparent font-bold text-[#fecaca]"
            >
              {translate("auth.common.close")}
            </button>
          ) : null}
        </div>
      ) : null}
      {children}
      {footer ? <div className="mt-4">{footer}</div> : null}
    </div>
  );
}

export function AuthSectionTitle({ title, description }) {
  return (
    <div className="mb-5">
      <div className="text-[1.45rem] font-extrabold text-slate-50">{title}</div>
      {description ? (
        <div className="mt-[0.45rem] text-[0.92rem] leading-[1.6] text-slate-400">
          {description}
        </div>
      ) : null}
    </div>
  );
}

const STATUS_TONE_CLASS = {
  info: "border-[rgba(143,211,255,0.32)] bg-[rgba(8,47,73,0.42)] text-[#8fd3ff]",
  success: "border-[rgba(52,211,153,0.32)] bg-[rgba(6,78,59,0.34)] text-emerald-300",
  error: "border-[rgba(248,113,113,0.3)] bg-[rgba(127,29,29,0.32)] text-[#fecaca]",
};

// The outcome of an auth step (account created, email verified, password
// updated, link expired), shown in place of the form. Actions go in children.
export function AuthStatusPanel({ tone = "info", icon: Icon, spinIcon = false, title, description, children }) {
  return (
    <div className="text-center">
      <div role={tone === "error" ? "alert" : "status"}>
        <div
          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border ${STATUS_TONE_CLASS[tone] || STATUS_TONE_CLASS.info}`}
        >
          {Icon ? <Icon size={26} aria-hidden="true" className={spinIcon ? "motion-safe:animate-spin" : undefined} /> : null}
        </div>
        <div className="text-[1.35rem] font-extrabold text-slate-50">{title}</div>
        {description ? (
          <div className="mt-2 text-[0.92rem] leading-[1.65] text-slate-300">{description}</div>
        ) : null}
      </div>
      {children ? <div className="mt-6 grid gap-3 text-left">{children}</div> : null}
    </div>
  );
}

export function AuthField({ label, children, hint }) {
  return (
    <div className="mb-3">
      <label className={authLabelClass}>{label}</label>
      {children}
      {hint ? <div className="mt-[0.45rem] text-[0.8rem] text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function PasswordChecklist({ password }) {
  const { language: currentLanguage } = useI18n();
  const translate = (key) => t(key);
  const rules = [
    { ok: password.length >= 8, text: translate("auth.password_rule.length") },
    { ok: /[A-Z]/.test(password), text: translate("auth.password_rule.uppercase") },
    { ok: /[a-z]/.test(password), text: translate("auth.password_rule.lowercase") },
    { ok: /\d/.test(password), text: translate("auth.password_rule.number") },
    { ok: PASSWORD_SYMBOL.test(password), text: translate("auth.password_rule.symbol") },
  ];
  // Only worth a line when broken: spaces or non-English characters.
  if (!PASSWORD_ALLOWED_CHARS.test(password)) {
    rules.push({ ok: false, text: translate("auth.password_rule.allowed_chars") });
  }

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {rules.map((rule) => (
        <span
          key={rule.text}
          className={`rounded-pill border px-2 py-0.5 text-[0.82rem] font-semibold ${
            rule.ok
              ? "border-[rgba(52,211,153,0.3)] text-emerald-300"
              : "border-[rgba(248,113,113,0.32)] text-[#fca5a5]"
          }`}
        >
          {rule.ok ? (currentLanguage === "ja" ? "○" : "✓") : (currentLanguage === "ja" ? "×" : "✘")} {rule.text}
        </span>
      ))}
    </div>
  );
}

// A password field with a show/hide toggle, so a typo can be checked without a
// second "confirm password" field.
export const PasswordInput = React.forwardRef(function PasswordInput({ className = "", ...inputProps }, ref) {
  const [isVisible, setIsVisible] = useState(false);
  useI18n();
  const label = t(isVisible ? "auth.field.hide_password" : "auth.field.show_password");
  return (
    <div className="relative">
      <input
        ref={ref}
        {...inputProps}
        type={isVisible ? "text" : "password"}
        className={`${authInputClass} pr-12 ${className}`.trim()}
      />
      <button
        type="button"
        onClick={() => setIsVisible((visible) => !visible)}
        aria-label={label}
        aria-pressed={isVisible}
        title={label}
        className="absolute inset-y-0 right-0 flex w-12 cursor-pointer items-center justify-center border-none bg-transparent text-slate-400 hover:text-slate-200"
      >
        {isVisible ? <FiEyeOff size={18} aria-hidden="true" /> : <FiEye size={18} aria-hidden="true" />}
      </button>
    </div>
  );
});

export function AuthFooterLinks({ prompt, linkTo, linkText, secondaryLink }) {
  return (
    <div className="mt-4 border-t border-[rgba(148,163,184,0.18)] pt-4 text-[0.92rem] leading-[1.7] text-slate-400">
      <div>
        {prompt}{" "}
        <Link to={linkTo} className={authPageLinkClass}>
          {linkText}
        </Link>
      </div>
      {secondaryLink ? <div className="mt-[0.4rem]">{secondaryLink}</div> : null}
    </div>
  );
}
